/**
 * Builds schema-valid dummy payloads for proctoring timer-end forced submit.
 * - draft_output_container for regular submit APIs
 * - submission-play-turn turn_data for Custom / response-generation tasks
 *
 * Rule: keep user-filled values; only fill dummy for empty required fields.
 */

import type {
    CustomGenerateResponsePayload,
    TurnDataType,
    UpdatedAttributesData,
    ValueType,
} from 'Types/Tasks.types'
import { AttributeDefinitionType, ContainerType, InputType } from 'Types/Tasks.types'
import { cloneDeep, isEmpty, isPlainObject as lodashIsPlainObject } from 'lodash'

/**
 * The only proctoring auto-submit marker ever written to a payload — always as a
 * VALUE (filling an otherwise-empty `{ value, value_type }` leaf), never as a key.
 * No `proctoring_auto_submitted` / `proctoring_dummy_payload` flag keys are added
 * anywhere in the payload — the backend rejects/mishandles unrecognized keys.
 */
export const PROCTORING_TIMEOUT_TEXT = 'proctoring auto submitted'
export const PROCTORING_TIMEOUT_DATE = '01/01/2020'

type PlayTurnUserKey =
    | 'domain'
    | 'subDomain'
    | 'timeToDevelopStrategy'
    | 'timeToSolve'
    | 'difficultyLevel'
    | 'complexityLevel'
    | 'accuracy'
    | 'humanFinalAnswer'
    | 'humanFinalAnswerReasoning'
    | 'humanCompleteFinalAnswer'
    | 'modelReasoningTrace'
    | 'modelReasoningTakenFrom'
    | 'modelFinalAnswer'

type PlayTurnScalarValue = string | number

type ModelFeedbackDetail = NonNullable<
    CustomGenerateResponsePayload['modelFeedbackDetails']
>[number]

type PlayTurnUserValues = Partial<
    Record<PlayTurnUserKey, PlayTurnScalarValue> & {
        modelFeedbackDetails: ModelFeedbackDetail[]
        turnDataType: TurnDataType
    }
>

type AttributeOption = { label?: string; value?: string }

type AttributeDefLike = {
    name?: string
    type?: AttributeDefinitionType
    input_definition?: {
        is_optional?: boolean
        input_type?: InputType
        options?: AttributeOption[]
        min?: number
    }
    value_definition?: { value_type?: ValueType }
}

enum SupplementConfidenceLevel {
    Confident = 'CONFIDENT',
}

enum SupplementStepName {
    Maker = 'Maker',
}

type JsonRecord = Record<string, unknown>

type AssignmentLike = {
    task?: JsonRecord & {
        attributes?: AttributeDefLike[]
        output_start_state_container?: JsonRecord
    }
}

const PLAY_TURN_USER_KEYS: PlayTurnUserKey[] = [
    'domain',
    'subDomain',
    'timeToDevelopStrategy',
    'timeToSolve',
    'difficultyLevel',
    'complexityLevel',
    'accuracy',
    'humanFinalAnswer',
    'humanFinalAnswerReasoning',
    'humanCompleteFinalAnswer',
    'modelReasoningTrace',
    'modelReasoningTakenFrom',
    'modelFinalAnswer',
]

const PROMPT_ANNOTATION_ENUM_DEFAULTS: Record<string, string> = {
    domain: 'Math',
    difficulty: 'EASY',
    complexity: '9-10_GRADE',
    accuracy: 'Both incorrect',
    human_final_answer: 'A',
    model_reasoning_taken_from: '1',
}

const DEFAULT_PROMPT_ANNOTATION: Record<string, unknown> = {
    accuracy: '',
    answer_explanation: '',
    complexity: '',
    difficulty: '',
    domain: '',
    feedback_level_metrics: null,
    human_complete_final_answer: '',
    human_final_answer: '',
    human_final_answer_reasoning: '',
    model_final_answer: '',
    model_reasoning_taken_from: '',
    model_reasoning_trace: '',
    skills: null,
    sub_domain: '',
    time_for_strategy: 0,
    time_to_solve: 0,
}

const STRUCTURAL_KEYS = new Set([
    'id',
    'quality_check',
    'version_id',
    'role',
    'source',
    'schema_version',
    'type',
    'value_type',
    'evaluation_on_response_version_id',
])

const FALLBACK_VERSION_ID = '00000000-0000-0000-0000-000000000001'
const STRING_VALUE_TYPE: ValueType = 'STRING'

const isBlank = (value: unknown): boolean =>
    value == null || (typeof value === 'string' && value.trim() === '')

const isPlainObject = (value: unknown): value is JsonRecord => lodashIsPlainObject(value)

const asString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined

const asNumber = (value: unknown): number | undefined =>
    typeof value === 'number' ? value : undefined

const fillText = (value: unknown, fallback = PROCTORING_TIMEOUT_TEXT): string =>
    isBlank(value) ? fallback : String(value)

const fillPositiveNumber = (value: unknown, fallback = 1): number => {
    const n = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(n) && n > 0 ? n : fallback
}

const isAttributeValueBlank = (value: unknown): boolean => {
    if (typeof value === 'number' || typeof value === 'boolean') return false
    if (typeof value === 'string') return isEmpty(value.trim())
    return isEmpty(value)
}

const ensureDecisionField = (container: Record<string, unknown>) => ({
    ...container,
    decision: container.decision ?? null,
})

const getDraftInner = (container: Record<string, unknown>): Record<string, unknown> | null => {
    if (!isPlainObject(container.data) || !isPlainObject(container.data.data)) return null
    return container.data.data as Record<string, unknown>
}

const readPromptAnnotation = (draft: unknown): Record<string, unknown> | null => {
    if (!isPlainObject(draft)) return null
    const data = isPlainObject(draft.data) ? draft.data : null
    const inner = data && isPlainObject(data.data) ? data.data : null
    const response = inner && isPlainObject(inner.response) ? inner.response : null
    const mainInfo = response && isPlainObject(response.main_info) ? response.main_info : null
    const value = mainInfo && isPlainObject(mainInfo.value) ? mainInfo.value : null
    if (!value) return null
    return isPlainObject(value.prompt_annotation) ? value.prompt_annotation : {}
}

export const collectPlayTurnValuesFromDraft = (draft: unknown): PlayTurnUserValues => {
    const ann = readPromptAnnotation(draft)
    if (!ann) return {}

    return {
        domain: asString(ann.domain),
        subDomain: asString(ann.sub_domain),
        timeToDevelopStrategy: asNumber(ann.time_for_strategy),
        timeToSolve: asNumber(ann.time_to_solve),
        difficultyLevel:
            typeof ann.difficulty === 'string' ? ann.difficulty.toLowerCase() : undefined,
        complexityLevel: asString(ann.complexity),
        accuracy: asString(ann.accuracy),
        humanFinalAnswer: asString(ann.human_final_answer),
        humanFinalAnswerReasoning: asString(ann.human_final_answer_reasoning),
        humanCompleteFinalAnswer: asString(ann.human_complete_final_answer),
        modelReasoningTrace: asString(ann.model_reasoning_trace),
        modelReasoningTakenFrom: asString(ann.model_reasoning_taken_from),
        modelFinalAnswer: asString(ann.model_final_answer),
    }
}

const pickPlayTurnValue = (
    formVal: PlayTurnScalarValue | undefined,
    draftVal: PlayTurnScalarValue | undefined
): PlayTurnScalarValue | undefined => {
    if (typeof formVal === 'number') return Number.isFinite(formVal) ? formVal : undefined
    if (!isBlank(formVal)) return formVal
    if (typeof draftVal === 'number') return Number.isFinite(draftVal) ? draftVal : undefined
    if (!isBlank(draftVal)) return draftVal
    return undefined
}

export const mergePlayTurnUserValues = (
    formValues: PlayTurnUserValues = {},
    draft?: unknown
): PlayTurnUserValues => {
    const fromDraft = collectPlayTurnValuesFromDraft(draft ?? null)
    const merged: PlayTurnUserValues = {}

    for (const key of PLAY_TURN_USER_KEYS) {
        const picked = pickPlayTurnValue(formValues[key], fromDraft[key])
        if (picked !== undefined) merged[key] = picked
    }

    if (Array.isArray(formValues.modelFeedbackDetails) && formValues.modelFeedbackDetails.length) {
        merged.modelFeedbackDetails = formValues.modelFeedbackDetails
    }

    return merged
}

export const isPlayTurnUserDataEmpty = (values: PlayTurnUserValues | null | undefined): boolean => {
    if (!values || typeof values !== 'object') return true
    return !PLAY_TURN_USER_KEYS.some(key => {
        const v = values[key]
        if (typeof v === 'string') return v.trim().length > 0
        if (typeof v === 'number') return Number.isFinite(v) && v > 0
        return false
    })
}

export const buildDummyPlayTurnGeneratePayload = (
    partial: PlayTurnUserValues = {}
): CustomGenerateResponsePayload => {
    const feedback = Array.isArray(partial.modelFeedbackDetails)
        ? partial.modelFeedbackDetails.map(detail => ({
              feedbackResultCode: detail?.feedbackResultCode ?? 0,
              feedbackUsefulness: fillText(detail?.feedbackUsefulness, 'Useful'),
              feedbackUsefulnessJustification: fillText(
                  detail?.feedbackUsefulnessJustification,
                  PROCTORING_TIMEOUT_TEXT
              ),
          }))
        : undefined

    return {
        turnDataType: partial.turnDataType || 'PROMPT_FINALIZATION',
        domain: fillText(partial.domain, 'Math'),
        subDomain: fillText(partial.subDomain, PROCTORING_TIMEOUT_TEXT),
        timeToDevelopStrategy: fillPositiveNumber(partial.timeToDevelopStrategy, 1),
        timeToSolve: fillPositiveNumber(partial.timeToSolve, 1),
        difficultyLevel: fillText(partial.difficultyLevel, 'easy'),
        complexityLevel: fillText(partial.complexityLevel, '9-10_GRADE'),
        accuracy: fillText(partial.accuracy, 'Both incorrect'),
        humanFinalAnswer: fillText(partial.humanFinalAnswer, 'A'),
        humanFinalAnswerReasoning: fillText(
            partial.humanFinalAnswerReasoning,
            PROCTORING_TIMEOUT_TEXT
        ),
        humanCompleteFinalAnswer: fillText(
            partial.humanCompleteFinalAnswer,
            PROCTORING_TIMEOUT_TEXT
        ),
        modelReasoningTrace: fillText(partial.modelReasoningTrace, PROCTORING_TIMEOUT_TEXT),
        modelReasoningTakenFrom: fillText(partial.modelReasoningTakenFrom, '1'),
        modelFinalAnswer: fillText(partial.modelFinalAnswer, PROCTORING_TIMEOUT_TEXT),
        ...(feedback?.length ? { modelFeedbackDetails: feedback } : {}),
    }
}

export const buildForcedPlayTurnGeneratePayload = (
    formValues: PlayTurnUserValues = {},
    draft?: unknown
) => {
    const userValues = mergePlayTurnUserValues(formValues, draft)
    return {
        payload: buildDummyPlayTurnGeneratePayload({
            ...userValues,
            turnDataType: formValues.turnDataType || 'PROMPT_FINALIZATION',
        }),
        usedDummy: isPlayTurnUserDataEmpty(userValues),
        userValues,
    }
}

export const buildDummyPlayTurnTurnData = (partial: PlayTurnUserValues = {}) => {
    const form = buildDummyPlayTurnGeneratePayload(partial)
    return {
        type: form.turnDataType,
        data: {
            domain: form.domain,
            sub_domain: form.subDomain,
            time_for_strategy: form.timeToDevelopStrategy,
            time_to_solve: form.timeToSolve,
            difficulty: String(form.difficultyLevel).toUpperCase(),
            complexity: form.complexityLevel,
            accuracy: form.accuracy,
            human_final_answer: form.humanFinalAnswer,
            human_final_answer_reasoning: form.humanFinalAnswerReasoning,
            human_complete_final_answer: form.humanCompleteFinalAnswer,
            model_reasoning_trace: form.modelReasoningTrace,
            model_reasoning_taken_from: form.modelReasoningTakenFrom,
            model_final_answer: form.modelFinalAnswer,
            ...(form.modelFeedbackDetails
                ? {
                      feedback_level_metrics: form.modelFeedbackDetails.map(detail => ({
                          feedback_usefulness: detail.feedbackUsefulness,
                          feedback_result_code: detail.feedbackResultCode,
                          feedback_usefulness_justification: detail.feedbackUsefulnessJustification,
                      })),
                  }
                : {}),
        },
    }
}

const readExistingAttributeEntry = (
    existing: Record<string, unknown> | null | undefined,
    name: string
): { value: unknown; value_type: unknown; value_id?: string } | null => {
    if (!existing || !isPlainObject(existing[name])) return null
    const entry = existing[name] as Record<string, unknown>
    if ('value' in entry && 'value_type' in entry) {
        return {
            value: entry.value,
            value_type: entry.value_type,
            value_id: asString(entry.value_id),
        }
    }
    if (isPlainObject(entry.data) && 'value' in entry.data && 'value_type' in entry.data) {
        return {
            value: entry.data.value,
            value_type: entry.data.value_type,
            value_id: asString(entry.value_id),
        }
    }
    return null
}

const isAttrEntryFilled = (current: unknown): boolean => {
    if (!isPlainObject(current)) return false
    if (isPlainObject(current.data) && !isAttributeValueBlank(current.data.value)) return true
    return 'value' in current && !isAttributeValueBlank(current.value)
}

const dummyValueForAttribute = (attr: AttributeDefLike): unknown => {
    const inputType = attr.input_definition?.input_type
    const valueType = attr.value_definition?.value_type
    const options = attr.input_definition?.options
    const first = Array.isArray(options) ? options.find(o => !isBlank(o?.value)) : undefined

    if (inputType === InputType.SELECT || inputType === InputType.MULTI_SELECT) {
        if (inputType === InputType.MULTI_SELECT || valueType === 'STRING_ARRAY') {
            return first?.value ? [String(first.value)] : [PROCTORING_TIMEOUT_TEXT]
        }
        return first?.value != null ? String(first.value) : PROCTORING_TIMEOUT_TEXT
    }
    if (inputType === InputType.DATE || valueType === 'DATE') return PROCTORING_TIMEOUT_DATE
    if (inputType === InputType.TIME || valueType === 'TIME') return '00:00:00'
    if (inputType === InputType.INT || inputType === InputType.INT_SCALE || valueType === 'INT') {
        const min = attr.input_definition?.min
        return typeof min === 'number' && Number.isFinite(min) ? min : 1
    }
    if (inputType === InputType.STRING_ARRAY || valueType === 'STRING_ARRAY') {
        return [PROCTORING_TIMEOUT_TEXT]
    }
    if (inputType === InputType.FILE || valueType === 'DOWNLOAD_FILE_MAPPING') {
        return { download_url: PROCTORING_TIMEOUT_TEXT, path: PROCTORING_TIMEOUT_TEXT }
    }
    return PROCTORING_TIMEOUT_TEXT
}

const eachAttrOfType = (
    attributeDefs: AttributeDefLike[] | undefined,
    typeName: AttributeDefinitionType,
    fn: (attr: AttributeDefLike) => void
) => {
    if (!attributeDefs) return
    for (const raw of attributeDefs) {
        if (!isPlainObject(raw)) continue
        const attr = raw as AttributeDefLike
        if (attr.type !== typeName || !attr.name) continue
        fn(attr)
    }
}

const fillAttrMap = (
    target: JsonRecord,
    attributeDefs: AttributeDefLike[] | undefined,
    typeName: AttributeDefinitionType,
    shape: 'flat' | 'wrapped'
) => {
    eachAttrOfType(attributeDefs, typeName, attr => {
        if (!attr.name || isAttrEntryFilled(target[attr.name])) return
        const value = dummyValueForAttribute(attr)
        const valueType = String(attr.value_definition?.value_type || STRING_VALUE_TYPE)
        target[attr.name] =
            shape === 'wrapped'
                ? { data: { value, value_type: valueType } }
                : { value_id: crypto.randomUUID(), value, value_type: valueType }
    })
}

export const fillProctoringTimeoutExtraInfo = (
    attributeDefs: AttributeDefLike[] | undefined,
    existingExtraInfo?: JsonRecord | UpdatedAttributesData
): UpdatedAttributesData => {
    const existing = isPlainObject(existingExtraInfo) ? existingExtraInfo : null
    const result: UpdatedAttributesData = {}

    if (existing) {
        for (const key of Object.keys(existing)) {
            const parsed = readExistingAttributeEntry(existing, key)
            if (!parsed || isAttributeValueBlank(parsed.value)) continue
            result[key] = existing[key] as UpdatedAttributesData[string]
        }
    }

    if (!attributeDefs) return result
    for (const raw of attributeDefs) {
        if (!isPlainObject(raw)) continue
        const attr = raw as AttributeDefLike
        if (attr.type && attr.type !== AttributeDefinitionType.EXTRA_INFO) continue
        if (!attr.name || isAttrEntryFilled(result[attr.name])) continue
        const prior = readExistingAttributeEntry(existing, attr.name)
        result[attr.name] = {
            value_id: prior?.value_id || crypto.randomUUID(),
            value: dummyValueForAttribute(attr),
            value_type: String(attr.value_definition?.value_type || STRING_VALUE_TYPE),
        } as UpdatedAttributesData[string]
    }

    return result
}

const fillQcVersionMap = (
    qc: JsonRecord,
    mapKey: string,
    versionKey: string,
    attributeDefs: AttributeDefLike[] | undefined,
    shouldEnsure: boolean
) => {
    if (!shouldEnsure && !(mapKey in qc)) return
    const map = isPlainObject(qc[mapKey]) ? { ...(qc[mapKey] as JsonRecord) } : {}
    const existingBucket = isPlainObject(map[versionKey])
        ? (map[versionKey] as JsonRecord)
        : (Object.values(map).find(isPlainObject) as JsonRecord | undefined)
    const bucket = { ...(existingBucket ?? {}) }
    fillAttrMap(bucket, attributeDefs, AttributeDefinitionType.QUALITY_EVALUATION, 'wrapped')
    if (Object.keys(bucket).length) {
        map[versionKey] = bucket
        qc[mapKey] = map
    } else if (!qc[mapKey]) {
        qc[mapKey] = {}
    }
}

const applyAttributeFills = (
    container: JsonRecord,
    attributeDefs: AttributeDefLike[] | undefined
) => {
    const inner = getDraftInner(container)
    if (!inner) return container

    if (isPlainObject(inner.response)) {
        const response = inner.response as JsonRecord
        response.extra_info = fillProctoringTimeoutExtraInfo(
            attributeDefs,
            isPlainObject(response.extra_info) ? response.extra_info : undefined
        )
    }
    if ('extra_info' in inner) {
        inner.extra_info = fillProctoringTimeoutExtraInfo(
            attributeDefs,
            isPlainObject(inner.extra_info) ? inner.extra_info : undefined
        )
    }

    if (!isPlainObject(inner.quality_check)) inner.quality_check = {}
    const qc = inner.quality_check as JsonRecord
    const response = isPlainObject(inner.response) ? (inner.response as JsonRecord) : null
    const versionKey =
        (response && (asString(response.version_id) || asString(response.id))) ||
        asString(inner.version_id) ||
        FALLBACK_VERSION_ID

    fillQcVersionMap(qc, 'evaluation_on_response_version_id', versionKey, attributeDefs, !!response)
    fillQcVersionMap(
        qc,
        'evaluation_on_version_id',
        versionKey,
        attributeDefs,
        'responses_ratings' in inner
    )

    if ('responses_ratings' in inner) {
        let hasRatingAttrs = false
        eachAttrOfType(attributeDefs, AttributeDefinitionType.RESPONSE_RATING, () => {
            hasRatingAttrs = true
        })
        if (hasRatingAttrs) {
            const list = Array.isArray(inner.responses_ratings)
                ? [...(inner.responses_ratings as unknown[])]
                : []
            if (!list.length) {
                const ratings: JsonRecord = {}
                fillAttrMap(
                    ratings,
                    attributeDefs,
                    AttributeDefinitionType.RESPONSE_RATING,
                    'wrapped'
                )
                list.push({ response_id: FALLBACK_VERSION_ID, ratings })
            } else {
                for (let i = 0; i < list.length; i++) {
                    const entry = list[i]
                    if (!isPlainObject(entry)) continue
                    const ratings = isPlainObject(entry.ratings)
                        ? { ...(entry.ratings as JsonRecord) }
                        : {}
                    fillAttrMap(
                        ratings,
                        attributeDefs,
                        AttributeDefinitionType.RESPONSE_RATING,
                        'wrapped'
                    )
                    entry.ratings = ratings
                    list[i] = entry
                }
            }
            inner.responses_ratings = list
        }
    }

    if (!response) return container
    const mainInfo = isPlainObject(response.main_info) ? (response.main_info as JsonRecord) : null
    const mainValue =
        mainInfo && isPlainObject(mainInfo.value) ? (mainInfo.value as JsonRecord) : null

    if (mainValue && Array.isArray(mainValue.turn_annotations)) {
        mainValue.turn_annotations = mainValue.turn_annotations.map(item => {
            if (!isPlainObject(item)) return item
            const attrs = isPlainObject(item.attributes)
                ? { ...(item.attributes as JsonRecord) }
                : {}
            fillAttrMap(attrs, attributeDefs, AttributeDefinitionType.TURN_EVALUATION, 'flat')
            return { ...item, attributes: attrs }
        })
    }

    if (mainValue && isPlainObject(mainValue.json_attributes)) {
        const jsonAttrs = mainValue.json_attributes as JsonRecord
        for (const [path, pathAttrs] of Object.entries(jsonAttrs)) {
            const map = isPlainObject(pathAttrs) ? { ...pathAttrs } : {}
            fillAttrMap(
                map,
                attributeDefs,
                AttributeDefinitionType.TURN_TARGETED_ATTRIBUTES,
                'flat'
            )
            jsonAttrs[path] = map
        }
    }

    for (const root of [response, inner]) {
        if (!isPlainObject(root.json_annotations)) continue
        const jsonAnnotations = root.json_annotations as JsonRecord
        for (const [path, node] of Object.entries(jsonAnnotations)) {
            if (!isPlainObject(node)) continue
            if (isPlainObject(node.attributes) || 'attributes' in node) {
                const attrs = isPlainObject(node.attributes)
                    ? { ...(node.attributes as JsonRecord) }
                    : {}
                fillAttrMap(
                    attrs,
                    attributeDefs,
                    AttributeDefinitionType.TARGETED_ATTRIBUTES,
                    'flat'
                )
                fillAttrMap(attrs, attributeDefs, AttributeDefinitionType.JSON_PATH, 'flat')
                jsonAnnotations[path] = { ...node, attributes: attrs }
            } else {
                const attrs = { ...node }
                fillAttrMap(attrs, attributeDefs, AttributeDefinitionType.JSON_PATH, 'flat')
                fillAttrMap(
                    attrs,
                    attributeDefs,
                    AttributeDefinitionType.TARGETED_ATTRIBUTES,
                    'flat'
                )
                jsonAnnotations[path] = attrs
            }
        }
    }

    return container
}

const ensureSupplement = (container: JsonRecord) => {
    const existing = isPlainObject(container.supplement)
        ? (container.supplement as JsonRecord)
        : null

    if (!existing) {
        container.supplement = {
            schema_version: 1,
            step_name: SupplementStepName.Maker,
            comment: { data: { value: PROCTORING_TIMEOUT_TEXT, value_type: STRING_VALUE_TYPE } },
            confidence_level: SupplementConfidenceLevel.Confident,
        }
        return container
    }

    if (!isPlainObject(existing.comment)) {
        existing.comment = {
            data: { value: PROCTORING_TIMEOUT_TEXT, value_type: STRING_VALUE_TYPE },
        }
    } else if (isPlainObject(existing.comment.data)) {
        const data = existing.comment.data as JsonRecord
        if (isEmpty(typeof data.value === 'string' ? data.value.trim() : data.value)) {
            data.value = PROCTORING_TIMEOUT_TEXT
            data.value_type = data.value_type || STRING_VALUE_TYPE
        }
    } else if (
        'value' in existing.comment &&
        isEmpty(
            typeof existing.comment.value === 'string'
                ? existing.comment.value.trim()
                : existing.comment.value
        )
    ) {
        existing.comment.value = PROCTORING_TIMEOUT_TEXT
        existing.comment.value_type = existing.comment.value_type || STRING_VALUE_TYPE
    }

    if (isBlank(existing.confidence_level)) {
        existing.confidence_level = SupplementConfidenceLevel.Confident
    }
    if (isBlank(existing.step_name)) existing.step_name = SupplementStepName.Maker
    container.supplement = existing
    return container
}

const fillEmptyJsonStateValues = (container: Record<string, unknown>) => {
    const inner = getDraftInner(container)
    if (!inner || !isPlainObject(inner.response)) return container
    const response = inner.response as Record<string, unknown>

    for (const key of ['state', 'main_info'] as const) {
        const node = response[key]
        if (!isPlainObject(node)) continue
        if (
            node.value_type === 'JSON' &&
            isPlainObject(node.value) &&
            !Object.keys(node.value).length
        ) {
            node.value = { timeout_submit: PROCTORING_TIMEOUT_TEXT }
        }
    }
    return container
}

const ensureCustomOutputMainInfo = (response: Record<string, unknown>) => {
    const mainInfo = isPlainObject(response.main_info)
        ? (response.main_info as Record<string, unknown>)
        : {}
    response.main_info = mainInfo

    if (!isPlainObject(mainInfo.value)) {
        mainInfo.value_type = mainInfo.value_type || 'JSON'
        mainInfo.value = {
            final_short_answer: '',
            human_correct_response: { steps: [] },
            human_response_needed: false,
            prompt: PROCTORING_TIMEOUT_TEXT,
            prompt_annotation: { ...DEFAULT_PROMPT_ANNOTATION },
            prompt_revisions: [],
            turns: [],
            version: 1,
        }
        return mainInfo
    }

    const value = mainInfo.value as Record<string, unknown>
    if (!isPlainObject(value.prompt_annotation)) {
        value.prompt_annotation = { ...DEFAULT_PROMPT_ANNOTATION }
    } else {
        for (const [key, defaultVal] of Object.entries(DEFAULT_PROMPT_ANNOTATION)) {
            if (!(key in value.prompt_annotation)) {
                ;(value.prompt_annotation as Record<string, unknown>)[key] = defaultVal
            }
        }
    }
    if (value.final_short_answer == null) value.final_short_answer = ''
    if (!Array.isArray(value.turns)) value.turns = []
    if (!Array.isArray(value.prompt_revisions))
        value.prompt_revisions = value.prompt_revisions ?? []
    if (isBlank(value.prompt)) value.prompt = PROCTORING_TIMEOUT_TEXT
    if (
        value.human_correct_response == null ||
        (isPlainObject(value.human_correct_response) &&
            (value.human_correct_response as Record<string, unknown>).steps == null)
    ) {
        value.human_correct_response = { steps: [] }
    }
    mainInfo.value_type = mainInfo.value_type || 'JSON'
    return mainInfo
}

const fillCustomOutputTimeoutFields = (container: JsonRecord) => {
    if (!isPlainObject(container.data)) return container
    if (container.data.type !== ContainerType.Custom || !isPlainObject(container.data.data))
        return container
    const response = container.data.data.response
    if (!isPlainObject(response)) return container

    const mainInfo = ensureCustomOutputMainInfo(response)
    const value = mainInfo.value as JsonRecord

    if (typeof value.final_short_answer !== 'string' || value.final_short_answer.trim() === '') {
        value.final_short_answer = PROCTORING_TIMEOUT_TEXT
    }

    const ann = value.prompt_annotation as JsonRecord
    for (const [key, current] of Object.entries(ann)) {
        if (typeof current === 'string' && current.trim() === '') {
            ann[key] = PROMPT_ANNOTATION_ENUM_DEFAULTS[key] ?? PROCTORING_TIMEOUT_TEXT
        } else if (
            (key === 'time_for_strategy' || key === 'time_to_solve') &&
            (typeof current !== 'number' || !Number.isFinite(current) || current <= 0)
        ) {
            ann[key] = 1
        }
    }
    return container
}

const fillBlankGenerationValue = (container: Record<string, unknown>) => {
    const inner = getDraftInner(container)
    if (!inner || !isPlainObject(inner.response)) return container
    const response = inner.response as Record<string, unknown>
    if (!isPlainObject(response.content) || !isPlainObject(response.content.data)) return container
    const contentData = response.content.data as Record<string, unknown>
    const contentValue = contentData.value
    if (contentValue == null || (typeof contentValue === 'string' && contentValue.trim() === '')) {
        contentData.value = PROCTORING_TIMEOUT_TEXT
        contentData.value_type = contentData.value_type || 'HTML'
    }
    return container
}

const applyAllTimeoutFills = (
    container: JsonRecord,
    attributeDefs: AttributeDefLike[] | undefined
): JsonRecord => {
    let result = fillBlankGenerationValue(container)
    result = fillCustomOutputTimeoutFields(result)
    result = fillEmptyJsonStateValues(result)
    result = ensureSupplement(result)
    result = applyAttributeFills(result, attributeDefs)
    if (result.decision === undefined) result.decision = null
    return result
}

export const buildDummyDraftOutputContainer = (
    assignment: AssignmentLike | null | undefined,
    emptyDraftContainer: JsonRecord | null | undefined
) => {
    const assignmentObj = isPlainObject(assignment) ? assignment : null
    const task = assignmentObj && isPlainObject(assignmentObj.task) ? assignmentObj.task : null
    const attributes = Array.isArray(task?.attributes)
        ? (task.attributes as AttributeDefLike[])
        : undefined
    const outputStart =
        task && isPlainObject(task.output_start_state_container)
            ? task.output_start_state_container
            : null

    if (isPlainObject(outputStart) && isPlainObject(outputStart.data)) {
        const cloned = cloneDeep(outputStart)
        return applyAllTimeoutFills(ensureDecisionField(cloned as JsonRecord), attributes)
    }

    const cloned = cloneDeep(emptyDraftContainer)
    if (!isPlainObject(cloned)) return emptyDraftContainer
    return applyAllTimeoutFills(ensureDecisionField(cloned), attributes)
}

const hasMeaningfulUserContent = (value: unknown, depth = 0): boolean => {
    if (depth > 8 || value == null) return false
    if (typeof value === 'string') return value.trim().length > 0
    if (typeof value === 'number' || typeof value === 'boolean') return true
    if (Array.isArray(value)) return value.some(item => hasMeaningfulUserContent(item, depth + 1))
    if (!isPlainObject(value)) return false
    return Object.entries(value).some(
        ([key, nested]) => !STRUCTURAL_KEYS.has(key) && hasMeaningfulUserContent(nested, depth + 1)
    )
}

export const isDraftEffectivelyEmpty = (draftOutputContainer: unknown): boolean => {
    if (!draftOutputContainer || !isPlainObject(draftOutputContainer)) return true
    const dataLayer = isPlainObject(draftOutputContainer.data) ? draftOutputContainer.data : null
    const data = dataLayer?.data
    if (data == null) return true
    if (typeof data !== 'object') return false
    return !hasMeaningfulUserContent(data)
}

const isValueLeaf = (node: unknown): node is { value: unknown; value_type: unknown } =>
    isPlainObject(node) && 'value' in node && 'value_type' in node

const fillEmptyValueLeaves = (node: unknown, depth = 0): unknown => {
    if (depth > 12 || node == null) return node
    if (Array.isArray(node)) return node.map(item => fillEmptyValueLeaves(item, depth + 1))
    if (!isPlainObject(node)) return node

    if (isValueLeaf(node)) {
        return typeof node.value === 'string' && node.value.trim() === ''
            ? { ...node, value: PROCTORING_TIMEOUT_TEXT }
            : node
    }

    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(node)) {
        result[key] = fillEmptyValueLeaves(val, depth + 1)
    }
    return result
}

export const fillProctoringTimeoutValues = <T>(
    draftOutputContainer: T,
    attributeDefs?: AttributeDefLike[]
): T => {
    const cloned = cloneDeep(draftOutputContainer)
    if (!isPlainObject(cloned)) return draftOutputContainer
    return applyAllTimeoutFills(fillEmptyValueLeaves(cloned) as JsonRecord, attributeDefs) as T
}
